// @flow

import * as React from 'react';

import {BodyMono} from 'sail/Text';
import {CompactTooltip} from 'sail/Tooltip';

import {useStripeClui} from './helpers/react/provider';

import type {SuggestionsType} from './helpers/react/reducer';

// Takes in a list of suggestions and displays it.
const StripeCluiSuggestions = ({
  suggestions,
  handleSuggestion,
  suggestionWrapperHeight,
  isPaneFocused,
}: {
  suggestions: SuggestionsType,
  handleSuggestion: (idx: number) => void,
  suggestionWrapperHeight: number,
  isPaneFocused: boolean,
}) => {
  const {
    state: {panes, focusedPane, isFocused: isShellFocused},
  } = useStripeClui();

  const {suggestionIndex, allowInput, displayOn, command, cursorIndex} = panes[
    focusedPane
  ];

  const selectedSuggestionRef = React.useRef<HTMLSpanElement | null>(null);
  const lastSuggestionRef = React.useRef<HTMLDivElement | null>(null);
  const suggestionsWrapperRef = React.useRef<HTMLDivElement | null>(null);

  // This effect handles scrolling the highlighted suggestion into view
  // as the user tabs.
  React.useEffect(() => {
    if (displayOn === 'suggestion' && selectedSuggestionRef.current) {
      selectedSuggestionRef.current.scrollIntoView({block: 'nearest'});
    }
  }, [suggestionIndex, displayOn]);

  // This effect ensures that suggestions are always in view. In the
  // case the suggestions overflow we show the maximum scrollable
  // content.
  React.useEffect(() => {
    if (
      suggestionsWrapperRef.current &&
      lastSuggestionRef.current &&
      allowInput
    ) {
      const lastItemBottom =
        lastSuggestionRef.current?.getBoundingClientRect().bottom ?? 0;
      const wrapperBottom =
        suggestionsWrapperRef.current?.getBoundingClientRect().bottom ?? 0;
      const windowHeight = document.body?.clientHeight ?? 0;

      // If the last item is below the bottom of the suggestion wrapper
      // then we know the suggestions exceed the visible height and
      // scroll the bottom of the wrapper into view.
      if (lastItemBottom > wrapperBottom) {
        suggestionsWrapperRef.current?.scrollIntoView(false); // eslint-disable-line no-unused-expressions
      }
      // Otherwise if the last suggestion is below the fold we scroll
      // it into view.
      else if (lastItemBottom > windowHeight) {
        lastSuggestionRef.current?.scrollIntoView(false); // eslint-disable-line no-unused-expressions
      }
    }
  }, [command, allowInput, cursorIndex, displayOn, suggestionIndex]);

  const suggestionList =
    isShellFocused && isPaneFocused && allowInput && !!suggestions?.length ? (
      suggestions.map<React.Node>((option, idx) => {
        let color = 'gray300';
        if (suggestionIndex >= 0 && idx === suggestionIndex) {
          color = 'white';
        }

        let outputOption = option[0];
        if (option[1] && option[1].required) {
          outputOption = `${option[0]} (required)`;
        }

        const suggestion = (
          <span
            ref={
              suggestionIndex >= 0 && idx === suggestionIndex
                ? selectedSuggestionRef
                : null
            }
          >
            <BodyMono color={color} wordBreak="all" key={idx}>
              {outputOption}
            </BodyMono>
          </span>
        );

        return (
          <div
            onClick={() => {
              handleSuggestion(idx);
            }}
            idx={idx}
            key={idx}
            role="option"
            style={{
              cursor: 'pointer',
            }}
            ref={idx === suggestions.length - 1 ? lastSuggestionRef : null}
          >
            {option[1] && option[1].description ? (
              <CompactTooltip
                variableWidth
                position="right"
                context={() => suggestion}
                label={String(option[1].description)}
              />
            ) : (
              suggestion
            )}
          </div>
        );
      })
    ) : (
      <></>
    );

  return (
    <div className="StripeCluiSuggestionsWrapper">
      <div
        className="StripeCluiSuggestionsScrollContainer"
        style={{height: suggestionWrapperHeight}}
        ref={suggestionsWrapperRef}
      >
        {suggestionList}
      </div>
    </div>
  );
};

export default StripeCluiSuggestions;
